<template>
  <nav class="flex-1 px-2 pb-4 space-y-1">
    <template v-for="item in navigationItems" :key="item.name">
      <div>
        <!-- Parent navigation item -->
        <div
          v-if="item.children"
          @click="toggleItem(item.name)"
          class="flex items-center justify-between cursor-pointer px-2 py-1.5 rounded-md group"
          :class="[
            isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
            'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
          ]"
        >
          <div class="flex items-center space-x-2">
            <component
              :is="item.icon"
              class="h-4 w-4"
              :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
              aria-hidden="true"
            />
            <span>{{ $t("Navigation." + item.name) }}</span>
          </div>
          <component
            :is="isExpanded(item.name) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            class="h-4 w-4 text-gray-400 transition-transform duration-200"
          />
        </div>
        <router-link
          v-else
          :to="item.to"
          class="flex items-center px-2 py-1.5 rounded-md group"
          :class="[
            isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
            'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
          ]"
        >
          <component
            :is="item.icon"
            class="h-4 w-4 mr-2"
            :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
            aria-hidden="true"
          />
          <span>{{ $t("Navigation." + item.name) }}</span>
        </router-link>

        <!-- Children navigation items -->
        <transition
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
          @after-leave="afterLeave"
        >
          <div v-if="item.children && isExpanded(item.name)" class="ml-5 border-l border-gray-200 pl-2 overflow-hidden">
            <router-link
              v-for="child in item.children"
              :key="child.name"
              :to="child.to"
              class="flex items-center px-3 py-1.5 text-sm rounded-md"
              :class="[
                isActiveRoute(child)
                  ? 'bg-primary-50 text-primary-dark font-medium'
                  : 'text-gray-500 hover:bg-gray-100',
              ]"
            >
              {{ $t("Navigation." + child.name) }}
            </router-link>
          </div>
        </transition>
      </div>
    </template>
  </nav>
</template>

<script>
import {
  DesktopComputerIcon,
  CollectionIcon,
  PresentationChartLineIcon,
  SparklesIcon,
  FireIcon,
  ArchiveIcon,
  ViewListIcon,
  UserCircleIcon,
  BookOpenIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/vue/outline";
import { mapGetters, mapState } from "vuex";

export default {
  components: {
    DesktopComputerIcon,
    CollectionIcon,
    PresentationChartLineIcon,
    SparklesIcon,
    FireIcon,
    ArchiveIcon,
    ViewListIcon,
    UserCircleIcon,
    BookOpenIcon,
    ChevronUpIcon,
    ChevronDownIcon,
  },
  data() {
    return {
      expandedItems: new Set(),
    };
  },
  computed: {
    ...mapState("auth", ["currentUser"]),
    ...mapGetters("auth", ["currentUserHasPermission", "isSeller", "isResponsible"]),
    ...mapGetters("store", ["selectedStore"]),
    isOfflineStore() {
      return this.selectedStore?.source === "offline";
    },
    navigation() {
      const items = [
        {
          name: "Subscription",
          to: "/pos",
          icon: DesktopComputerIcon,
        },
        this.leadsNavigationItem,
        this.performancesNavigationItem,
        {
          name: "Retributions",
          to: "/pos/retributions/pending",
          icon: SparklesIcon,
          children: [
            {
              name: "Pending",
              to: "/pos/retributions/pending",
            },
            {
              name: "Paid",
              to: "/pos/retributions/paid",
            },
            {
              name: "Retributions grid",
              to: "/pos/retribution-configurations",
            },
          ],
        },
        {
          name: "Challenge",
          to: "/pos/challenge/current",
          icon: FireIcon,
          children: [
            {
              name: "Current challenge",
              to: "/pos/challenge/current",
            },
            {
              name: "Challenge history",
              to: "/pos/challenge/challenge-list",
            },
          ],
        },
        {
          name: "Plan sales",
          to: "/pos/plan-sales",
          icon: ArchiveIcon,
        },
        {
          name: "Claim List",
          to: "/pos/claims",
          icon: ViewListIcon,
          children: [
            {
              name: "Claim management",
              to: "/pos/claims",
            },
            {
              name: "Claim invoices",
              to: "/pos/claim_invoices",
            },
          ],
        },
        {
          name: "Sellers",
          to: "/pos/sellers",
          icon: UserCircleIcon,
        },
        {
          name: "Offers",
          to: "/pos/contract-distributions",
          icon: BookOpenIcon,
        },
      ];

      if (!this.isResponsible) return items;
      return items.sort((a, b) => {
        const order = ["Performances", "Subscription", "Leads"];
        const indexA = order.indexOf(a.name);
        const indexB = order.indexOf(b.name);
        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
      });
    },
    performancesNavigationItem() {
      const item = {
        name: "Performances",
        icon: PresentationChartLineIcon,
        to: "/pos/analytics/roadmap",
        children: [
          {
            name: "Roadmap",
            to: "/pos/analytics/roadmap",
          },
          {
            name: "General analytics",
            to: "/pos/analytics/general",
          },
          {
            name: "Retail analytics",
            to: "/pos/analytics/retail",
          },
        ],
      };
      const sellerPerformances = {
        ...item,
        to: "/pos/analytics/roadmap",
        children: [
          {
            name: "Roadmap",
            to: "/pos/analytics/roadmap",
          },
          {
            name: "Retail analytics",
            to: "/pos/analytics/retail",
          },
        ],
      };
      return this.isSeller ? sellerPerformances : item;
    },
    leadsNavigationItem() {
      const item = {
        name: "Leads",
        to: "/leads/index",
        icon: CollectionIcon,
      };
      if (this.isOfflineStore) {
        item.children = [
          {
            name: "All leads",
            to: "/leads/index",
          },
          {
            name: "Leads Waiting For confirmation / Payment Failed",
            to: "/leads/processing",
            navigation: "LeadsFailed",
          },
        ];
      }
      return item;
    },
    navigationItems() {
      return this.navigation.filter((item) => {
        if (item.name === "Retributions") return this.currentUserHasPermission("retributions");
        return true;
      });
    },
  },
  methods: {
    isActiveRoute(item) {
      if (item.to === this.$route.path) return true;
      if (item.to === "/pos") return false; // Ignore pos navigation item
      if (this.$route.path.startsWith(item.to + "/")) return true;
      if (item.children) return item.children.some((child) => this.isActiveRoute(child));
      return false;
    },
    toggleItem(itemName) {
      if (this.expandedItems.has(itemName)) {
        this.expandedItems.delete(itemName);
      } else {
        this.expandedItems.clear();
        this.expandedItems.add(itemName);
      }
    },
    isExpanded(itemName) {
      return this.expandedItems.has(itemName);
    },
    // Transition hooks for smooth slide
    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
    },
    enter(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    afterEnter(el) {
      el.style.height = "auto";
      el.style.transition = "";
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    leave(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = "0";
      el.style.opacity = "0";
    },
    afterLeave(el) {
      el.style.transition = "";
      el.style.height = "auto";
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
